import { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

import WorkOrderEnumMultipleField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumMultipleField';
import WorkOrderEnumSingleField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumSingleField';
import WorkOrdersContext, {
  WorkOrdersInputActionTypes,
} from '@atom/components/workOrders/WorkOrdersContext';
import { GET_WORK_ORDER_TEMPLATE } from '@atom/graph/workTemplate';
import { DataType } from '@atom/types/dataType';
import { FieldFilter, WorkOrderField } from '@atom/types/work';
import { CustomFieldView } from '@atom/utilities/taskUtilities';
import {
  convertDateToLocalDayEnd,
  convertDateToLocalDayStart,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import { isCascadingField } from '@atom/utilities/workOrderFieldUtilities';

import WorkOrdersFiltersDatePicker from './WorkOrdersFiltersDatePicker';
import WorkOrdersFiltersNumericRange from './WorkOrdersFiltersNumericRange';

import classes from './workOrdersFilters.module.css';

const WorkOrdersWorkFieldsFilter = () => {
  const { workOrdersInputCart, dispatch } = useContext(WorkOrdersContext);

  const [fields, setFields] = useState<WorkOrderField[]>([]);
  const [getWorkOrderTemplate, { data: workOrderTemplateData }] = useLazyQuery<
    { workOrderTemplate: { fields } },
    { id: string }
  >(GET_WORK_ORDER_TEMPLATE);

  useEffect(() => {
    if (workOrderTemplateData) {
      setFields(workOrderTemplateData.workOrderTemplate.fields);
    }
  }, [workOrderTemplateData]);

  useEffect(() => {
    if (workOrdersInputCart.workTemplateIds.length === 1) {
      getWorkOrderTemplate({
        variables: {
          id: workOrdersInputCart.workTemplateIds.join(''),
        },
      });
    } else if (workOrdersInputCart.workTemplateIds.length !== 1) {
      setFields(null);
    }
  }, [workOrdersInputCart.workTemplateIds]);

  const handleDateStartChange =
    (id?: string, fieldProperty?: keyof FieldFilter) => (_, val: any) => {
      const dateMillis = val ? convertDateToLocalDayStart(val) : null;

      dispatch({
        type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_FIELD,
        data: {
          fieldId: id,
          fieldProperty,
          value: dateMillis,
        },
      });
    };

  const handleDateEndChange =
    (id?: string, fieldProperty?: keyof FieldFilter) => (_, val: any) => {
      const dateMillis = val ? convertDateToLocalDayEnd(val) : null;

      dispatch({
        type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_FIELD,
        data: {
          fieldId: id,
          fieldProperty,
          value: dateMillis,
        },
      });
    };

  const handleNumericFieldChange =
    (id?: string, fieldProperty?: keyof FieldFilter) => (_, val: any) => {
      const value = val === '' ? null : Number(val);

      dispatch({
        type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_FIELD,
        data: {
          fieldId: id,
          fieldProperty,
          value,
        },
      });
    };

  const handleChange =
    (id?: string, fieldProperty?: keyof FieldFilter) => (_, val: any) => {
      const value = val ? val.valueOf() : null;

      dispatch({
        type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_FIELD,
        data: {
          fieldId: id,
          fieldProperty,
          value,
        },
      });
    };

  const getDateValue = (id?: string, fieldProperty?: keyof FieldFilter) => {
    const foundField: any = workOrdersInputCart.fields.find(
      field => field.id === id,
    );

    if (foundField && !isNilOrEmpty(foundField[fieldProperty])) {
      return new Date(Number(foundField[fieldProperty]));
    }

    return null;
  };

  const getField = (id?: string) => {
    const foundField: any = workOrdersInputCart.fields.find(
      field => field.id === id,
    );

    return foundField || null;
  };

  const getContent = (field: WorkOrderField) => {
    if (isCascadingField(field)) {
      return <div />;
    }

    const components = {
      [DataType.DATE]: (
        <div className={classes.customFieldContainer}>
          <WorkOrdersFiltersDatePicker
            field={field}
            startDateValue={getDateValue(field.id, 'valueStart')}
            endDateValue={getDateValue(field.id, 'valueEnd')}
            onStartDateChange={handleDateStartChange(field.id, 'valueStart')}
            onEndDateChange={handleDateEndChange(field.id, 'valueEnd')}
          />
        </div>
      ),
      [DataType.DATE_TIME]: (
        <div className={classes.customFieldContainer}>
          <WorkOrdersFiltersDatePicker
            field={field}
            startDateValue={getDateValue(field.id, 'valueStart')}
            endDateValue={getDateValue(field.id, 'valueEnd')}
            onStartDateChange={handleChange(field.id, 'valueStart')}
            onEndDateChange={handleChange(field.id, 'valueEnd')}
          />
        </div>
      ),
      [DataType.ENUM_SINGLE]: (
        <div className={classes.customFieldContainer}>
          <WorkOrderEnumSingleField
            field={{ ...field, value: getField(field.id)?.value }}
            onChange={handleChange(field.id, 'value')}
            customFieldView={CustomFieldView.FILTER}
          />
        </div>
      ),
      [DataType.ENUM_MULTIPLE]: (
        <div className={classes.customFieldContainer}>
          <WorkOrderEnumMultipleField
            field={{ ...field, value: getField(field.id)?.values }}
            onChange={handleChange(field.id, 'values')}
            customFieldView={CustomFieldView.FILTER}
          />
        </div>
      ),
      [DataType.NUMBER]: (
        <div className={classes.customFieldContainer}>
          <WorkOrdersFiltersNumericRange
            field={field}
            startValue={getField(field.id)?.valueStart}
            endValue={getField(field.id)?.valueEnd}
            onStartChange={handleNumericFieldChange(field.id, 'valueStart')}
            onEndChange={handleNumericFieldChange(field.id, 'valueEnd')}
          />
        </div>
      ),
    };

    return components[field.dataType];
  };

  return <>{fields.map(field => getContent(field))}</>;
};

export default WorkOrdersWorkFieldsFilter;
